"use client";

import React from "react";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { Logos } from "@/data";
import { useScrollReveal } from "@/components/useScrollReveal";
import { ShieldCheck, GraduationCap, Network, Headset, Wallet, BarChart3, LucideIcon } from "lucide-react";

const cardIcons = [ShieldCheck, GraduationCap, Network, Headset, Wallet, BarChart3];

function FloatingCard({
  icon: Icon,
  text,
  className = "",
}: {
  icon: LucideIcon;
  text: string;
  className?: string;
}) {
  return (
    <div className={`absolute w-48 xl:w-56 animate-float ${className}`}>
      <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-white/95 backdrop-blur-md ring-1 ring-black/5 border border-white shadow-[0_12px_28px_-8px_rgba(6,150,168,0.35)]">
        <span className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-[#0696a8] to-[#0ea5e9] text-white shadow-md shadow-[#0696a8]/40">
          <Icon size={18} strokeWidth={2.2} />
        </span>
        <p className="text-sm text-slate-700 font-semibold leading-snug mt-1.5">{text}</p>
      </div>
    </div>
  );
}

export default function WhyChooseUs() {
  const { t } = useTranslation();
  const revealRef = useScrollReveal();

  const cards = [
    t("whyChooseUs.card.card1"), t("whyChooseUs.card.card2"),
    t("whyChooseUs.card.card3"), t("whyChooseUs.card.card4"),
    t("whyChooseUs.card.card5"), t("whyChooseUs.card.card6"),
  ];

  const leftPositions = [
    "top-[8%] -left-6 xl:-left-16 animation-delay-0 [animation-duration:6.5s]",
    "top-[42%] -left-10 xl:-left-24 animation-delay-500 [animation-duration:8s]",
    "top-[76%] -left-6 xl:-left-16 animation-delay-1000 [animation-duration:7.2s]",
  ];
  const rightPositions = [
    "top-[8%] -right-6 xl:-right-16 animation-delay-300 [animation-duration:7.8s]",
    "top-[42%] -right-10 xl:-right-24 animation-delay-700 [animation-duration:6.8s]",
    "top-[76%] -right-6 xl:-right-16 animation-delay-150 [animation-duration:7.5s]",
  ];

  return (
    <section id="about" className="relative py-20 bg-slate-50 overflow-hidden">
      {/* Radar pattern: concentric rings, softer color, fading outward from center */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "repeating-radial-gradient(circle at 50% 50%, rgba(100,140,150,0.14) 0, rgba(100,140,150,0.14) 1px, transparent 2px, transparent 46px)",
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 55% at 50% 50%, black 0%, transparent 85%)",
          maskImage:
            "radial-gradient(ellipse 70% 55% at 50% 50%, black 0%, transparent 85%)",
        }}
      />
      {/* Strong white fade top & bottom so pattern blends into page background */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-slate-50 via-slate-50/80 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-50 via-slate-50/80 to-transparent" />

      <div ref={revealRef} className="relative w-[92%] max-w-[1800px] mx-auto px-5 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12 lg:mb-16">
          <h2 className="text-h2 md:text-[2.2rem] text-slate-900 leading-tight tracking-tight mb-6">
            {t("whyChooseUs.title")}
          </h2>
          <p className="text-body lg:text-base text-slate-600">
            {t("whyChooseUs.desc")}
          </p>
        </div>

        {/* Desktop: image with floating cards */}
        <div className="relative mx-auto hidden lg:block max-w-4xl lg:min-h-[560px]">
          <div className="absolute inset-0 bg-[#0696a8]/10 blur-3xl rounded-full scale-90 -z-10" />

          <div className="relative mx-auto w-full max-w-md">
            <Image
              src={Logos.edunavMobile}
              alt="Aplikasi Mobile Edunav"
              width={640} height={640}
              className="w-full object-contain drop-shadow-2xl"
            />
          </div>

          {cards.slice(0, 3).map((text, i) => (
            <FloatingCard key={i} icon={cardIcons[i]} text={text} className={leftPositions[i]} />
          ))}

          {cards.slice(3, 6).map((text, i) => (
            <FloatingCard key={i} icon={cardIcons[i + 3]} text={text} className={rightPositions[i]} />
          ))}
        </div>

        {/* Mobile / tablet fallback: image + static grid */}
        <div className="lg:hidden flex flex-col items-center gap-10">
          <Image
            src={Logos.edunavMobile}
            alt="Aplikasi Mobile Edunav"
            width={500} height={500}
            className="w-full max-w-sm object-contain drop-shadow-2xl"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
            {cards.map((text, index) => {
              const Icon = cardIcons[index];
              return (
                <div key={index} className="flex items-start gap-3 p-4 rounded-xl bg-white shadow-sm border border-slate-100">
                  <span className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-[#0696a8]/10 text-[#0696a8]">
                    <Icon size={20} />
                  </span>
                  <p className="text-sm text-gray-700 font-medium leading-relaxed mt-1.5">{text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
